import {onMounted, ref, watch, Ref, onUnmounted, nextTick, getCurrentInstance} from "vue"
import {useConfigStore} from "../../app/store/config"
import {CrudListOptions} from '@hc/graphql'
import {Logger} from '@hc/base'

export type UseSchemaListParams = CrudListOptions & {

}

export function useSchemaList<
  T=any,
  TInsert=any,
  TUpdate=any,
  TWhere=any,
  TOrder=any
>(collectionName: string, initialParams?: UseSchemaListParams, loadOnMount = true){

  const config = useConfigStore()
  const collection = config.api.crudCollection(collectionName)

  const list = ref<T[]>([]) as Ref<T[]>
  const loading = ref<boolean>(false)
  const count = ref<number>(null)
  const subscription = ref(null)
  const params = ref<UseSchemaListParams>({
    filter: null,
    where: null,
    order: null,
    limit: null,
    offset: null,
    search: null,
    skipJoins: null,
    includeDefaultJoins: null,
    ignoreJoins: null,
    includeJoins: null,
    fields: null,
    apiToken: null,
    debug: null,
    aggregate: null,
    ...initialParams
  }) as Ref<UseSchemaListParams>

  const logger = new Logger(`SchemaList`, () => ({collection: collection.name}))

  async function load(){
    if(!collection.canSelect) return
    loading.value = true
    try {
      //logger.log("Load list", params.value)
      if (collection.canAggregate) count.value = await collection.count(params.value)
      list.value = await collection.list(params.value)
    } catch(e){
      logger.exception(e,"Error loading list %o: %s",params.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  function clear(){
    list.value = null
  }

  function subscribe() {
    logger.log('subscribe', params.value)
    subscription.value = collection.subscribeList(params.value, {
      result: (result) => {
        logger.log('refresh from subscription', params.value, result)
        list.value = result[0]
      },
      error: (e) => {
        logger.exception(e,"Error loading list %o: %s",params.value)
        throw e
      }
    })
  }

  function unsubscribe() {
    if(!subscription.value) return
    logger.log('unsubscribe', params.value)
    subscription.value?.unsubscribe()
  }

  function insert(item: T){
    logger.log("Inserting object", item)
    list.value = list.value || []
    list.value.push(item)
  }

  function remove(item: T){
    logger.log("Removing object", item)
  }

  async function create(data: TInsert): Promise<number|null>{
    loading.value = true
    try {
      logger.log("Create object", params.value, data)
      const result = await collection.create(data, params.value)
      logger.log("Created object", result)
      insert(result)
      return result.id
    } catch(e){
      logger.exception(e,"Error creating object",params.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function update(id: number|string, data: TUpdate): Promise<boolean>{
    //@ts-ignore
    const index = list.value.findIndex((i) => i.id == id)
    if(index < 0){
      const e = new Error('Item to update is not in list: ' +id)
      logger.exception(e, e.message)
    }
    loading.value = true
    try {
      logger.log("Update object", id, data)
      const result  = await collection.update(Number(id), data, params.value)
      // objects index might have been changed during async call
      //@ts-ignore
      const index = list.value.findIndex((i) => i.id == id)
      Object.assign(list.value[index], result)
      logger.log("Object updated", id, result)
      return true
    } catch(e){
      logger.exception(e,"Error updating object",id, data)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function destroy(id: number|string): Promise<boolean> {
    //@ts-ignore
    const index = list.value.findIndex((i) => i.id == id)
    if(index < 0){
      const e = new Error('Item to delete is not in list: ' +id)
      logger.exception(e, e.message)
    }
    loading.value = true
    try {
      logger.log("Deleting object", id)
      const result = await collection.delete(Number(id), {skipReturn: true})
      // objects index might have been changed during async call
      //@ts-ignore
      const index = list.value.findIndex((i) => i.id == id)
      list.value.splice(index, 1)
      logger.log("Deleted object", id)
      return true
    } catch(e){
      logger.exception(e,"Error deleting object",id)
      throw e
    } finally {
      loading.value = false
    }
  }

  watch(params, async () => {
    //logger.log("Params changed %o", params.value)
    load()
  }, {deep: true})

  onMounted(async function(){
    if( loadOnMount )
      await load()
  })

  return {
    list,
    count,
    params,
    collection,
    loading,
    load,
    create,
    update,
    destroy,
    clear,
    subscribe,
    unsubscribe
  }
}
